import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Button,
  Divider,
  List,
  ListItem,
  Avatar,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  InputAdornment,
  styled
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  ArticleOutlined as ArticleIcon,
  InfoOutlined as InfoIcon,
  CodeOutlined as CodeIcon,
  DesignServicesOutlined as DesignIcon,
  SecurityOutlined as SecurityIcon,
  ApiOutlined as ApiIcon,
  ViewListOutlined as FeaturesIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import conversationService from '../../services/conversationService';
import useNotification from '../../hooks/useNotification';
import useDocuments from '../../hooks/useDocuments';
import QuestionCard, { parseAskUserContent } from '../common/QuestionCard';
import ToolActivityBlock from '../common/ToolActivityBlock';
import { parseToolActivity } from '../../utils/parseToolActivity';

/**
 * A chat interface component for the PRD workflow
 * Provides streaming chat functionality when generating content
 */
const WorkflowChat = ({ 
  sectionName,
  projectId, 
  existingContent = '',
  onSaveContent,
  disabled = false,
  placeholder = "Type your instructions here...",
  relatedSections = {}, // Contains content from related sections
  streamHandler = null // Optional handlers for streaming events
}) => {
  // State for chat
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [showRelatedSections, setShowRelatedSections] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);
  // Store the entire conversation history as it develops
  const [conversationHistory, setConversationHistory] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { showNotification } = useNotification();
  const { createDocument, saveMdFile, getProjectDocuments } = useDocuments();

  // Reset messages when the section changes
  useEffect(() => {
    // Clear messages when switching sections
    setMessages([]);
    setStreamingResponse('');
    setConversationHistory([]);
    
    // If there's existing content, show it as a system message
    if (existingContent) {
      setMessages([{
        role: 'system',
        content: `Current content for ${sectionName}:\n\n${existingContent}`,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [existingContent, sectionName]);

  // Create a conversation if needed
  useEffect(() => {
    if (!conversationId && projectId && !disabled) {
      const createNewConversation = async () => {
        try {
          console.log(`Creating new conversation for ${sectionName}`);
          
          // Get task_id from URL parameters if available
          const urlSearchParams = new URLSearchParams(window.location.search);
          const taskId = urlSearchParams.get('task');
          
          // Use optimized get-or-create endpoint
          const newConversation = await conversationService.getOrCreateConversation(
            projectId,
            taskId || sectionName.toLowerCase().replace(/\s+/g, '-'),
            `${sectionName} Generation`
          );
          console.log(`Created new conversation: ${newConversation._id}`);
          
          // Set the conversation ID in local state
          setConversationId(newConversation._id);
        } catch (error) {
          console.error('Error creating conversation:', error);
          showNotification({
            message: `Error creating conversation: ${error.message}`,
            type: 'error'
          });
        }
      };
      
      createNewConversation();
    }
  }, [conversationId, projectId, sectionName, disabled, showNotification]);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingResponse]);

  // Update conversation history when messages or streaming response changes
  useEffect(() => {
    // Build complete history from all assistant messages
    const assistantHistory = messages
      .filter(msg => msg.role === 'assistant')
      .map(msg => msg.content);
    
    // Add streaming response if available
    if (streamingResponse.trim()) {
      setConversationHistory([...assistantHistory, streamingResponse]);
    } else {
      setConversationHistory(assistantHistory);
    }

    // Log when conversation history is updated
    console.log(`Updated conversation history: ${assistantHistory.length} messages${streamingResponse ? ' + streaming' : ''}`);
  }, [messages, streamingResponse]);

  // Handle sending new message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !conversationId || isLoading) return;
    
    const userMessage = {
      role: 'user',
      content: newMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(messages => [...messages, userMessage]);
    setNewMessage('');
    setIsLoading(true);
    setIsStreaming(true);
    
    try {
      // Clear any previous streaming response
      setStreamingResponse('');
      
      // Get the section type (normalize it for API)
      const sectionType = sectionName;
      console.log(`Using context-aware streaming for section: ${sectionType}`);
      
      // Stream the message with context from related sections
      await conversationService.streamMessageWithContext(
        conversationId,
        newMessage,
        (chunk, fullText) => {
          // Update streaming response as chunks arrive
          setStreamingResponse(prev => prev + chunk);
          
          // If custom stream handler is provided, call it with chunk data
          if (streamHandler && typeof streamHandler.onChunk === 'function') {
            streamHandler.onChunk(chunk, fullText);
          }
        },
        (completeResponse) => {
          // When streaming is complete, add the full response to messages
          const assistantMessage = {
            role: 'assistant',
            content: completeResponse,
            timestamp: new Date().toISOString()
          };
          setMessages(messages => [...messages, assistantMessage]);
          setStreamingResponse('');
          setIsStreaming(false);
          setIsLoading(false);
          
          // Call custom complete handler if provided
          if (streamHandler && typeof streamHandler.onComplete === 'function') {
            streamHandler.onComplete(completeResponse);
          }
        },
        (error) => {
          console.error('Streaming error:', error);
          showNotification({
            message: `Error: ${error.message}`,
            type: 'error'
          });
          setIsStreaming(false);
          setIsLoading(false);
          
          // Call custom error handler if provided
          if (streamHandler && typeof streamHandler.onError === 'function') {
            streamHandler.onError(error);
          }
        },
        sectionType,
        relatedSections
      );
    } catch (error) {
      console.error('Error sending message:', error);
      showNotification({
        message: `Error sending message: ${error.message}`,
        type: 'error'
      });
      setIsLoading(false);
      setIsStreaming(false);
    }
  };

  // Helper function to process feature content and format with accordion style
  const processFeatureContent = (content) => {
    try {
      console.log("Processing feature content, length:", content.length);
      // Log first 100 chars for debugging
      console.log("Feature content preview:", content.substring(0, 100) + "...");

      // Look for feature sections in the text
      const featureRegex = /[Ff]eature\s*(?:[:#-]\s*)?([A-Za-z0-9_\s\-.,]+)|\b(F\d{3})[\s:]+([A-Za-z0-9_\s\-.,]+)/g;
      let features = [];
      let match;
      let featureIdCounter = 1;
      
      // Collect all feature titles
      while ((match = featureRegex.exec(content)) !== null) {
        const featureTitle = match[1] || match[3];
        if (featureTitle && !features.some(f => f.title.trim() === featureTitle.trim())) {
          features.push({
            id: match[2] || `F${String(featureIdCounter).padStart(3, '0')}`,
            title: featureTitle.trim(),
            original: match[0]
          });
          featureIdCounter++;
        }
      }
      
      console.log(`Found ${features.length} features in the content`);
      features.forEach((f, idx) => console.log(`Feature ${idx+1}: ${f.id} - ${f.title}`));
      
      // If we found features, format them with accordions
      if (features.length > 0) {
        // Start with a header
        let formattedContent = "# Features\n\n";
        
        // For each feature, create an accordion section
        features.forEach(feature => {
          // Escape special characters in the original text for regex
          const escapedOriginal = feature.original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          
          // Regex to find content related to this feature
          const featureContentRegex = new RegExp(`${escapedOriginal}([\\s\\S]*?)(?=(?:[Ff]eature\\s*(?:[:#-]\\s*)?|F\\d{3}[\\s:]+|$))`, 'i');
          const contentMatch = featureContentRegex.exec(content);
          let featureContent = contentMatch ? contentMatch[1].trim() : '';
          
          // Add the formatted feature with its ID
          formattedContent += `## ${feature.id}: ${feature.title}\n\n`;
          if (featureContent) {
            formattedContent += `${featureContent}\n\n`;
          }
        });
        
        console.log(`Formatted content with ${features.length} features`);
        return formattedContent.trim();
      }
    } catch (error) {
      console.error('Error processing feature content:', error);
    }
    
    // Return original content if processing fails
    console.warn('No features found or processing failed, returning original content');
    return content;
  };
  
  // Helper function to process user story content and format with accordion style
  const processUserStoryContent = (content) => {
    try {
      console.log("Processing user story content, length:", content.length);
      // Log first 100 chars for debugging
      console.log("User story content preview:", content.substring(0, 100) + "...");

      // Look for user story sections in the text (expanded pattern)
      const storyRegex = /(?:(?:[Uu]ser\s*[Ss]tory)|(?:[Aa]s\s+a))[\s:]*(?:[:#\-\s]*)?([^\.]*?)(?:[\.,]|$)|(?:US\d{3})[\s:]+([^\.]*?)(?:[\.,]|$)/g;
      let stories = [];
      let match;
      let storyIdCounter = 1;
      
      // Collect all user story titles
      while ((match = storyRegex.exec(content)) !== null) {
        // Get the title from whichever group matched
        let storyTitle = '';
        for (let i = 1; i < match.length; i++) {
          if (match[i]) {
            storyTitle = match[i].trim();
            break;
          }
        }
        
        if (storyTitle && !stories.some(s => s.title.trim() === storyTitle.trim())) {
          // Check if the match contains an ID like "US001"
          const idMatch = match[0].match(/\b(US\d{3})\b/);
          const storyId = idMatch ? idMatch[1] : `US${String(storyIdCounter).padStart(3, '0')}`;
          
          stories.push({
            id: storyId,
            title: storyTitle,
            original: match[0]
          });
          storyIdCounter++;
        }
      }
      
      console.log(`Found ${stories.length} user stories in the content`);
      stories.forEach((s, idx) => console.log(`Story ${idx+1}: ${s.id} - ${s.title}`));
      
      // If we found user stories, format them with accordions
      if (stories.length > 0) {
        // Start with a header
        let formattedContent = "# User Stories\n\n";
        
        // For each user story, create an accordion section
        stories.forEach(story => {
          // Escape special characters in the original text for regex
          const escapedOriginal = story.original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          
          // Regex to find content related to this story - look for content until the next story or end
          const storyContentRegex = new RegExp(`${escapedOriginal}([\\s\\S]*?)(?=(?:(?:[Uu]ser\\s*[Ss]tory)|(?:[Aa]s\\s+a))|(?:US\\d{3})|$)`, 'i');
          const contentMatch = storyContentRegex.exec(content);
          let storyContent = contentMatch ? contentMatch[1].trim() : '';
          
          // Add the formatted story with its ID
          formattedContent += `## ${story.id}: ${story.title}\n\n`;
          if (storyContent) {
            formattedContent += `${storyContent}\n\n`;
          }
        });
        
        console.log(`Formatted content with ${stories.length} user stories`);
        return formattedContent.trim();
      }
    } catch (error) {
      console.error('Error processing user story content:', error);
    }
    
    // Return original content if processing fails
    console.warn('No user stories found or processing failed, returning original content');
    return content;
  };

  // Handle save content from chat
  const handleSaveContent = () => {
    // Use the accumulated conversation history from our state
    // This ensures we have all messages, not just what's currently displayed
    const allContent = conversationHistory || [];
    
    console.log(`Saving content for ${sectionName}, with ${allContent.length} parts`);
    
    // If there's nothing to save, show a warning and return
    if (allContent.length === 0) {
      showNotification({
        message: 'No content to save',
        type: 'warning'
      });
      return;
    }
    
    // Join all parts of the conversation
    let contentToSave = allContent.join('\n\n');
    
    // For Features and User Stories, format the content in accordion style with IDs
    if (['Features', 'User Stories'].includes(sectionName)) {
      console.log(`Formatting ${sectionName} with full content, length: ${contentToSave.length}`);
      
      if (sectionName === 'Features') {
        // Process the content to ensure features have proper IDs (F001, F002, etc.)
        const processedContent = processFeatureContent(contentToSave);
        if (processedContent) {
          contentToSave = processedContent;
        }
      } else if (sectionName === 'User Stories') {
        // Process the content to ensure user stories have proper IDs (US001, US002, etc.)
        const processedContent = processUserStoryContent(contentToSave);
        if (processedContent) {
          contentToSave = processedContent;
        }
      }
    }
    
    // Only proceed if we have content to save and a save handler
    if (contentToSave && onSaveContent) {
      console.log(`Final content to save, length: ${contentToSave.length} characters`);
      
      // First, save content via the parent handler (updates UI state)
      // Handle both Promise and non-Promise returns from onSaveContent
      const saveResult = onSaveContent(contentToSave);
      
      // Create a Promise chain (if onSaveContent doesn't return a Promise, we'll create one)
      Promise.resolve(saveResult)
        .then(() => {
          // Then create document in the backend database
          if (!projectId) {
            throw new Error('Project ID is required to save document');
          }
          
          // Map section names to valid document types
          let documentType;
          switch (sectionName) {
            case 'Field Validations':
              documentType = 'validation';
              break;
            case 'Technology Stack':
              documentType = 'tech_stack';
              break;
            case 'Development Guidelines':
              documentType = 'dev_guidelines';
              break;
            case 'Design Guidelines':
              documentType = 'design_guidelines';
              break;
            case 'Security Guidelines':
              documentType = 'security_guidelines';
              break;
            case 'API Guidelines':
              documentType = 'api_guidelines';
              break;
            case 'Features':
              documentType = 'features';
              break;
            case 'User Stories':
              documentType = 'user_stories';
              break;
            case 'Final PRD':
              documentType = 'final_prd';
              break;
            default:
              documentType = sectionName.toLowerCase().replace(/\s+/g, '_');
          }
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const documentTitle = `${sectionName} - ${timestamp}`;
          const filename = `${sectionName.toLowerCase().replace(/\s+/g, '-')}-${timestamp}.md`;
          
          // First create document record in database
          return createDocument(projectId, {
            title: documentTitle,
            content: contentToSave,
            type: documentType
          }).then(newDocument => {
            console.log('Created document:', newDocument);
            
            // Also save as markdown file
            return saveMdFile(projectId, filename, contentToSave);
          }).then(savedFile => {
            console.log('Saved markdown file:', savedFile);
              
            showNotification({
              message: `${sectionName} content saved as document successfully`,
              type: 'success'
            });
          });
        })
        .catch(error => {
          console.error('Error saving content:', error);
          showNotification({
            message: `Error saving content: ${error.message}`,
            type: 'error'
          });
        });
    } else {
      showNotification({
        message: 'No content to save',
        type: 'warning'
      });
    }
  };

  // Handle key press (Enter to send)
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Get section-specific prompt
  const getSectionPrompt = () => {
    // Custom prompts for specific sections that build on each other
    const sectionPrompts = {
      "Field Validations": 
        `I'll help you define validation rules based on the PRD. ${
          existingContent 
            ? "I've loaded the current validation rules above for reference." 
            : "We'll analyze the PRD to identify all fields that need validation and define appropriate rules for each."
        } Please describe the scope of validations needed or ask me to suggest validation rules based on the PRD.`,
      
      "Technology Stack": 
        `I'll help you define the appropriate technology stack based on both the PRD and Field Validations. ${
          existingContent 
            ? "I've loaded the current technology recommendations above for reference." 
            : "We'll consider the validation requirements when selecting technologies and frameworks."
        } What specific requirements or constraints should we consider?`,
      
      "Development Guidelines": 
        `I'll help you create comprehensive development guidelines based on the PRD and Technology Stack. ${
          existingContent 
            ? "I've loaded the current content above for reference." 
            : "We'll define coding standards, practices, and patterns appropriate for the selected technologies."
        } What specific development aspects would you like to focus on?`,
        
      "Design Guidelines": 
        `I'll help you create design guidelines based on the PRD and Field Validations. ${
          existingContent 
            ? "I've loaded the current design guidelines above for reference." 
            : "We'll ensure the design supports all the required field validations and user interactions."
        } What aspects of the design do you want to define?`,
        
      "Security Guidelines": 
        `I'll help you create security guidelines based on the PRD, Technology Stack, and Field Validations. ${
          existingContent 
            ? "I've loaded the current security guidelines above for reference." 
            : "We'll address security considerations for your specific technologies and data validation needs."
        } I have access to the Technology Stack and Development Guidelines to provide relevant security recommendations. What security concerns or requirements do you have?`,
        
      "API Guidelines": 
        `I'll help you define API guidelines based on the PRD, Development Guidelines, and Technology Stack. ${
          existingContent 
            ? "I've loaded the current API guidelines above for reference." 
            : "We'll ensure the APIs follow the development standards and support the required functionality."
        } I have access to the Technology Stack and Development Guidelines to provide API recommendations that align with your chosen technologies. What API design principles or specifications would you like to follow?`,
        
      "Features": 
        `I'll help you define features based on the PRD and previous sections. ${
          existingContent 
            ? "I've loaded the current feature list above for reference." 
            : "We'll create a comprehensive feature list that aligns with all previous specifications."
        } I have access to Field Validations, Technology Stack, Development Guidelines, Design Guidelines, Security Guidelines, and API Guidelines to ensure all features are well-aligned with your project's requirements and constraints. What specific features would you like to prioritize or define?`,
        
      "User Stories": 
        `I'll help you create user stories based on the Features and previous sections. ${
          existingContent 
            ? "I've loaded the current user stories above for reference." 
            : "We'll write user stories that cover all the features and incorporate all requirements."
        } I have access to Features, Field Validations, Technology Stack, Development Guidelines, Design Guidelines, Security Guidelines, and API Guidelines to ensure comprehensive and aligned user stories. What user perspectives or journeys would you like to focus on?`,
        
      "Final PRD": 
        `I'll help you compile a comprehensive final PRD that integrates all previous sections into separate markdown files. ${
          existingContent 
            ? "I've loaded the current content above for reference." 
            : "This will create individual .md files for validation, development, technology, design, API, security, user stories, and a main PRD file that agents can use for development."
        } How would you like to organize the final documentation?`
    };
    
    // Return custom prompt or default prompt
    return sectionPrompts[sectionName] || 
      `I'll help you generate content for the ${sectionName} section. ${
        existingContent 
          ? "I've loaded the current content above so you can see what's already there." 
          : "There's no existing content yet. Let me know what you'd like to include."
      }`;
  };
  
  // Get the appropriate initial prompt
  const initialPrompt = getSectionPrompt();
  
  // Get relevant section dependencies based on current section
  const getRelevantSections = () => {
    switch (sectionName) {
      case 'Design Guidelines':
        return ['Field Validations', 'Technology Stack'];
        
      case 'Security Guidelines':
        return ['Technology Stack', 'Development Guidelines'];
        
      case 'API Guidelines':
        return ['Technology Stack', 'Development Guidelines'];
        
      case 'Features':
        return [
          'Field Validations', 
          'Technology Stack', 
          'Development Guidelines', 
          'Design Guidelines', 
          'Security Guidelines', 
          'API Guidelines'
        ];
        
      case 'User Stories':
        return [
          'Features',
          'Field Validations', 
          'Technology Stack', 
          'Development Guidelines', 
          'Design Guidelines', 
          'Security Guidelines', 
          'API Guidelines'
        ];
        
      case 'Final PRD':
        return [
          'Features',
          'User Stories',
          'Field Validations', 
          'Technology Stack', 
          'Development Guidelines', 
          'Design Guidelines', 
          'Security Guidelines', 
          'API Guidelines'
        ];
        
      default:
        return [];
    }
  };
  
  // Get section icon based on name
  const getSectionIcon = (sectionName) => {
    switch (sectionName) {
      case 'Field Validations':
        return <ArticleIcon />;
      case 'Technology Stack':
        return <CodeIcon />;
      case 'Development Guidelines':
        return <InfoIcon />;
      case 'Design Guidelines':
        return <DesignIcon />;
      case 'Security Guidelines':
        return <SecurityIcon />;
      case 'API Guidelines':
        return <ApiIcon />;
      case 'Features':
        return <FeaturesIcon />;
      case 'User Stories':
        return <PersonIcon />;
      case 'Final PRD':
        return <ArticleIcon />;
      default:
        return <InfoIcon />;
    }
  };

  // Styled components for new chat layout
  const MessageList = styled(Box)(({ theme }) => ({
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    scrollBehavior: 'smooth',
    maxWidth: '900px',
    margin: '0 auto',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  }));

  const ChatBubble = styled(Paper)(({ theme, role, isStreaming }) => ({
    padding: theme.spacing(2),
    maxWidth: '100%', // Full width for all messages
    width: '100%', // Full width for all messages
    borderRadius: '8px',
    backgroundColor: role === 'user' ? 'rgba(234, 240, 246, 1)' : 'white', // Light blue for user, white for assistant
    color: theme.palette.text.primary, // Same text color for both
    wordBreak: 'break-word',
    boxShadow: 'none', // No shadow for either
    border: role === 'user' ? '1px solid rgba(200, 210, 220, 0.8)' : '1px solid rgba(230, 230, 230, 1)', // Border for both
    marginBottom: theme.spacing(3), // Add spacing between messages
    ...(isStreaming && {
      backgroundColor: 'rgba(244, 250, 244, 0.9)', // Very light green background for streaming
      borderLeft: '2px solid rgba(76, 175, 80, 0.5)' // Just a left accent border for streaming
    }),
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%', // Full width on mobile
    }
  }));

  const ChatMessageContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%'
  }));

  const InputContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    position: 'sticky',
    bottom: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  }));

  return (
    <Paper elevation={1} sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6">{sectionName} Assistant</Typography>
        <Tooltip title="Save content from this chat">
          <span>
            <Button
              startIcon={<SaveIcon />}
              variant="outlined"
              onClick={handleSaveContent}
              disabled={disabled || (!streamingResponse && messages.filter(m => m.role === 'assistant').length === 0)}
            >
              Save Content
            </Button>
          </span>
        </Tooltip>
      </Box>
      
      {/* Related sections panel - only show if there are relevant sections */}
      {getRelevantSections().length > 0 && showRelatedSections && (
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Related Sections Reference
            </Typography>
            <Chip 
              label={showRelatedSections ? "Hide References" : "Show References"}
              size="small"
              onClick={() => setShowRelatedSections(!showRelatedSections)}
              color="primary"
              variant="outlined"
            />
          </Box>
          
          {getRelevantSections().map((section) => (
            <Accordion 
              key={section}
              expanded={expandedSection === section}
              onChange={() => setExpandedSection(expandedSection === section ? null : section)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.light', mr: 1, width: 24, height: 24 }}>
                    {getSectionIcon(section)}
                  </Avatar>
                  <Typography variant="body2">{section}</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ maxHeight: '150px', overflow: 'auto' }}>
                  {relatedSections[section] ? (
                    <ReactMarkdown>
                      {relatedSections[section]}
                    </ReactMarkdown>
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center">
                      No content available for {section}
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
      
      {/* Chat messages area */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden'
      }}>
        <MessageList ref={messagesEndRef}>
          {/* System prompt for context - simplified layout */}
          <Box sx={{ mb: 4 }}>
            <Box mb={1} display="flex" alignItems="center">
              <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28, mr: 1.5 }}>
                <BotIcon fontSize="small" />
              </Avatar>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Assistant
              </Typography>
            </Box>
            
            <ChatBubble role="assistant">
              <Typography variant="body2">
                {initialPrompt}
              </Typography>
              
              {/* Show reference toggle when there are relevant sections */}
              {getRelevantSections().length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button 
                    size="small"
                    variant="text" 
                    onClick={() => setShowRelatedSections(!showRelatedSections)}
                  >
                    {showRelatedSections ? "Hide References" : "Show References"}
                  </Button>
                </Box>
              )}
            </ChatBubble>
          </Box>
          
          {/* Message list - simplified layout */}
          {messages.filter(msg => msg.role !== 'system').map((message, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              {/* Message sender info */}
              <Box mb={1} display="flex" alignItems="center">
                {message.role === 'assistant' ? (
                  <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28, mr: 1.5 }}>
                    <BotIcon fontSize="small" />
                  </Avatar>
                ) : (
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 28, height: 28, mr: 1.5 }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                )}
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {message.role === 'user' ? (message.createdByName || message.created_by_name || 'You') : 'Assistant'}
                </Typography>
              </Box>
              
              {/* Message content */}
              <ChatBubble role={message.role} isStreaming={false}>
                {message.role === 'assistant' ? (
                  (() => {
                    const content = message.content || '';

                    // Detect ask_user questions — render as interactive QuestionCard
                    const askUserData = parseAskUserContent(content);
                    if (askUserData) {
                      return (
                        <QuestionCard
                          questions={askUserData.questions}
                          context={askUserData.context}
                          disabled={isLoading}
                          onSubmit={(formattedAnswer) => {
                            // Set the answer in the input and trigger send
                            setNewMessage(formattedAnswer);
                            // Use a micro-delay so React processes the state update
                            setTimeout(() => {
                              document.querySelector('[data-chat-submit]')?.click();
                            }, 50);
                          }}
                        />
                      );
                    }

                    // Detect artifact content: long + starts with ## headings or has many ## sections
                    const isArtifact = content.length > 2000 && (content.match(/^##\s/gm) || []).length >= 3;

                    if (isArtifact) {
                      // Show brief summary for artifact content — full content is in the split view
                      const sectionCount = (content.match(/^##\s/gm) || []).length;
                      const firstLine = content.split('\n').find(l => l.trim() && !l.startsWith('#')) || '';
                      return (
                        <Box sx={{
                          p: 2, backgroundColor: 'rgba(71, 89, 105, 0.04)',
                          borderRadius: 1, border: '1px solid rgba(71, 89, 105, 0.1)',
                        }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                            Document generated — {sectionCount} sections
                          </Typography>
                          {firstLine && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                              {firstLine.replace(/\*\*/g, '').slice(0, 150)}{firstLine.length > 150 ? '...' : ''}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
                            View full content in the panel on the right →
                          </Typography>
                        </Box>
                      );
                    }

                    const segments = parseToolActivity(content);
                    const markdownSx = {
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        fontWeight: 600,
                        marginTop: 2,
                        marginBottom: 1,
                      },
                      '& h1': { fontSize: '1.5rem' },
                      '& h2': { fontSize: '1.3rem' },
                      '& h3': { fontSize: '1.1rem' },
                      '& p': { marginBottom: 1.5 },
                      '& ul, & ol': { paddingLeft: 3, marginBottom: 1.5 },
                      '& li': { marginBottom: 0.5 },
                      '& code': {
                        backgroundColor: '#f5f5f5',
                        padding: '2px 4px',
                        borderRadius: 4,
                        fontSize: '0.9em',
                      },
                      '& pre': {
                        backgroundColor: '#f5f5f5',
                        padding: 1.5,
                        borderRadius: 4,
                        overflowX: 'auto',
                        marginBottom: 1.5,
                      },
                      '& blockquote': {
                        borderLeft: '3px solid #ccc',
                        paddingLeft: 1.5,
                        marginLeft: 0,
                        marginBottom: 1.5,
                        fontStyle: 'italic',
                      }
                    };

                    return (
                      <Box>
                        {segments.map((seg, segIdx) =>
                          seg.type === 'tool' ? (
                            <ToolActivityBlock key={segIdx} tools={seg.tools} />
                          ) : (
                            <Box key={segIdx} sx={markdownSx}>
                              <ReactMarkdown>{seg.content}</ReactMarkdown>
                            </Box>
                          )
                        )}
                      </Box>
                    );
                  })()
                ) : (
                  <Typography variant="body1">
                    {message.content}
                  </Typography>
                )}
                
                {/* Action buttons for assistant messages */}
                {message.role === 'assistant' && (
                  <Box sx={{ 
                    display: 'flex', 
                    mt: 1.5,
                    justifyContent: 'flex-end',
                    borderTop: '1px solid rgba(0,0,0,0.05)',
                    pt: 1
                  }}>
                    <Tooltip title="Copy message">
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          navigator.clipboard.writeText(message.content);
                          showNotification({
                            message: "Message copied to clipboard",
                            type: "success"
                          });
                        }}
                        sx={{ mr: 1 }}
                      >
                        <MenuBookIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </ChatBubble>
            </Box>
          ))}
          
          {/* Streaming response - simplified layout */}
          {isStreaming && streamingResponse && (
            <Box sx={{ mb: 2 }}>
              {/* Message sender info */}
              <Box mb={1} display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', width: 28, height: 28, mr: 1.5 }}>
                  <BotIcon fontSize="small" />
                </Avatar>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Assistant
                </Typography>
              </Box>
              
              {/* Message content */}
              <ChatBubble role="assistant" isStreaming={true}>
                {(() => {
                  const segments = parseToolActivity(streamingResponse);
                  const streamMdSx = {
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      fontWeight: 600, marginTop: 2, marginBottom: 1
                    },
                    '& p': { marginBottom: 1.5 },
                    '& ul, & ol': { paddingLeft: 3, marginBottom: 1.5 },
                    '& li': { marginBottom: 0.5 }
                  };
                  return (
                    <Box>
                      {segments.map((seg, segIdx) =>
                        seg.type === 'tool' ? (
                          <ToolActivityBlock key={segIdx} tools={seg.tools} />
                        ) : (
                          <Box key={segIdx} sx={streamMdSx}>
                            <ReactMarkdown>{seg.content}</ReactMarkdown>
                          </Box>
                        )
                      )}
                    </Box>
                  );
                })()}

                <Typography
                  variant="body2"
                  color="primary.main"
                  sx={{ mt: 1, display: 'flex', alignItems: 'center' }}
                >
                  <span style={{ color: '#2196f3', marginRight: '6px' }}>●</span>
                  Assistant is generating...
                </Typography>
              </ChatBubble>
            </Box>
          )}
          
          {/* Auto-scroll anchor */}
          <div ref={messagesEndRef} />
        </MessageList>
        
        {/* Input area */}
        <InputContainer>
          <Box sx={{ maxWidth: '1200px', width: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Action buttons row */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 1,
              borderRadius: '20px',
              backgroundColor: 'rgba(0,0,0,0.03)',
              p: 0.75,
              px: 1.5
            }}>
              {/* Show references button */}
              {getRelevantSections().length > 0 && (
                <Tooltip title={showRelatedSections ? "Hide References" : "Show References"}>
                  <IconButton 
                    size="small" 
                    onClick={() => setShowRelatedSections(!showRelatedSections)}
                    sx={{ mr: 1 }}
                  >
                    <ArticleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            
            {/* Input field with integrated send button */}
            <Box sx={{ width: '100%', position: 'relative' }}>
              <TextField
                fullWidth
                placeholder={placeholder}
                variant="outlined"
                size="medium"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading || disabled}
                inputRef={inputRef}
                multiline
                maxRows={4}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        data-chat-submit="true"
                        onClick={handleSendMessage}
                        color="primary"
                        disabled={!newMessage.trim() || isLoading || disabled}
                        sx={{ 
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'primary.dark',
                          },
                          '&.Mui-disabled': {
                            bgcolor: 'action.disabledBackground',
                            color: 'action.disabled',
                          },
                          mr: 0.5
                        }}
                      >
                        {isLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SendIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '30px',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.1)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.2)',
                    },
                    '&.Mui-focused fieldset': {
                      borderWidth: '1px',
                      borderColor: 'rgba(25, 118, 210, 0.5)',
                    },
                    pr: 0.5 // Reduce right padding to make room for the button
                  }
                }}
              />
            </Box>
          </Box>
        </InputContainer>
      </Box>
    </Paper>
  );
};

export default WorkflowChat;